import { Component, effect, inject, signal, viewChild, WritableSignal } from '@angular/core';
import {
  artworkFilters,
  FilterComponent,
  LikeButtonComponent,
} from "@components";
import { rxResource } from "@angular/core/rxjs-interop";
import { UserService } from "@api-services";
import { NgOptimizedImage } from "@angular/common";
import { IconDirective, IconMapPinLightComponent } from "@icons";
import { Router, RouterLink } from "@angular/router";
import { UserDto } from "@api-contracts";
import { map, tap } from "rxjs/operators";
import { debouncedSignal } from "../../../utils";
import { DisplayNamePipe } from "../../../pipes/display-name.pipe";

@Component({
  selector: 'wun-artists',
  imports: [
    FilterComponent,
    NgOptimizedImage,
    RouterLink,
    IconMapPinLightComponent,
    IconDirective,
    LikeButtonComponent,
    DisplayNamePipe
  ],
  templateUrl: './artists.component.html',
  styleUrl: './artists.component.scss'
})
export class ArtistsComponent {
  isGallery(): boolean {
    return this.router.url.includes('galleries');
  }

  private readonly artistsService = inject(UserService)
  private readonly router = inject(Router);
  filter = viewChild(FilterComponent);
  filterSignal: WritableSignal<artworkFilters | undefined> = signal({
    price: {
      from: 0,
      to: 100000
    },
    categories: [],
    subCategories: [],
    name: '',
    type: undefined,
    tags: []
  });

  filterDebounce = debouncedSignal(this.filterSignal, 400)

  _artists = rxResource({
    params: this.filterDebounce,
    stream: params => this.search(params.params)
  })

  artists = signal<UserDto[]>([])

  /** Cities of the listed users, suggested in the location filter. */
  private readonly knownCities = signal<string[]>([]);
  locations = this.knownCities.asReadonly();

  init: boolean = true;
  private debounceTime: number | undefined;

  constructor() {
    effect(() => {
      const filter = this.filter();
      if (filter) this.setFilters(filter.filter())
    });
  }

  setFilters(filters: artworkFilters) {
    clearTimeout(this.debounceTime)
    this.debounceTime = setTimeout(() => {
      if (!this.init)
        this.filterSignal.set(filters);
      else
        this.init = false;
    }, 300) as any;
  }

  search(request = this.filterSignal()) {
    const gallery = this.isGallery();
    const and: any[] = [{status: 'ACTIVE'}];

    // Free text: every word must match a name, the city, the interests or one of the artworks (title / tags).
    const words = (request?.name || '').trim().split(/\s+/).filter(Boolean).slice(0, 8);
    for (const word of words) {
      const term = `%${word}%`;
      and.push({
        $or: [
          {firstName: {$ilike: term}},
          {lastName: {$ilike: term}},
          {nickname: {$ilike: term}},
          ...(gallery ? [{galleryName: {$ilike: term}}] : []),
          {city: {$ilike: term}},
          {topics: {$ilike: term}},
          {artworks: {name: {$ilike: term}}},
        ]
      });
    }

    if (request?.location) {
      and.push({
        $or: [
          {city: {$ilike: `%${request.location}%`}},
          {country: {$ilike: `%${request.location}%`}},
        ]
      });
    }

    if (!gallery && request?.mediums?.length) {
      // Artists having at least one published artwork in the selected mediums.
      and.push({artworks: {technique: {$in: request.mediums}, status: 'EVALUATION_ACCEPTED', deletedAt: null}});
    }

    const args = {
      includes: JSON.stringify(['cover', 'covers']),
      pageSize: 1000,
      sorting: JSON.stringify(
        request?.sortBy === 'mostLiked' ? {likeCount: 'DESC'} :
        request?.sortBy === 'recent' ? {createdAt: 'DESC'} :
        {score: 'DESC'}
      ),
      filters: JSON.stringify({$and: and})
    }
    const req = this.router.url.includes('galleries') ? this.artistsService.usersGetGalleries(args) : this.artistsService.usersGetArtists(args);
    return req.pipe(
      map(d => {
        return {
          hasNext: d.hasNext,
          hasPrevious: d.hasPrevious,
          data: d.data.map(d => {
            return {
              ...d,
              cover: d.covers.find(cover => cover.ref === d.cover!.ref)
            } as UserDto
          }),
          totalCount: d.totalCount,
        }
      }),
      tap(d => {
        this.artists.set(d.data || []);
        const cities = new Set(this.knownCities());
        (d.data || []).forEach(u => u.city?.trim() && cities.add(u.city.trim()));
        if (cities.size !== this.knownCities().length)
          this.knownCities.set([...cities].sort((a, b) => a.localeCompare(b)));
      })
    )
  }
}
